import React, { useCallback } from 'react'
import { useOutletContext } from 'react-router-dom'
import { Card, Typography, Flex, Form, Input, Select, Spin } from 'antd'
import { ApiOutlined, SearchOutlined } from '@ant-design/icons'
import { type PixelPlatform, pixelPlatformOptions } from '@/data'
import { usePartnerSearch } from '@/utils/usePartnerSearch'
import type { PixelEditorContext } from '../../index'

const { Title } = Typography

export const SettingsPage: React.FC = () => {
  const {
    name,
    setName,
    platform,
    setPlatform,
    pixel_id,
    setPixelId,
    api_token,
    setApiToken,
    partner_id,
    setPartnerId,
  } = useOutletContext<PixelEditorContext>()

  const {
    options: partnerOptions,
    loading: partnersLoading,
    onSearch,
    loadMore,
  } = usePartnerSearch()

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.target as HTMLDivElement
      if (target.scrollTop + target.clientHeight >= target.scrollHeight - 40) {
        loadMore()
      }
    },
    [loadMore],
  )

  return (
    <div>
      <Title level={4}>Pixel Settings</Title>

      <Card style={{ maxWidth: 600 }}>
        <Form layout="vertical">
          <Form.Item label="Pixel Name" required>
            <Input
              placeholder="e.g., Partner 1 - Main Pixel"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Form.Item>

          <Form.Item label="Platform" required>
            <Select
              placeholder="Select a platform"
              value={platform}
              onChange={(value: PixelPlatform) => setPlatform(value)}
              options={pixelPlatformOptions}
            />
          </Form.Item>

          <Form.Item label="Partner" required>
            <Select
              showSearch
              placeholder="Search for a partner..."
              value={partner_id ?? undefined}
              onChange={(value: number) => setPartnerId(value)}
              onSearch={onSearch}
              onPopupScroll={handleScroll}
              options={partnerOptions}
              suffixIcon={<SearchOutlined />}
              filterOption={false}
              loading={partnersLoading}
              notFoundContent={
                partnersLoading ? <Spin size="small" /> : 'No matches'
              }
              dropdownRender={(menu) => (
                <>
                  {menu}
                  {partnersLoading && partnerOptions.length > 0 && (
                    <Flex justify="center" style={{ padding: 8 }}>
                      <Spin size="small" />
                    </Flex>
                  )}
                </>
              )}
            />
          </Form.Item>

          <Form.Item label="Pixel ID" required>
            <Input
              placeholder="Enter Pixel ID (e.g., 485729103847561)"
              value={pixel_id}
              onChange={(e) => setPixelId(e.target.value)}
            />
          </Form.Item>

          <Form.Item label="API Token" required>
            <Input.Password
              placeholder="Enter Access Token"
              value={api_token}
              onChange={(e) => setApiToken(e.target.value)}
              addonBefore={<ApiOutlined />}
            />
          </Form.Item>
        </Form>
      </Card>
    </div>
  )
}
